import { getErrorMessage } from '@web-platform/global-components-utils/error-helpers/getErrorMessage';

import { formatCurrencies, formatLocales, formatMarkets } from '../data';

import type {
  CultureCurrencyResponse,
  CultureLocaleResponse,
  CultureMarketResponse,
  FetchedCultureData,
} from '@web-platform/global-components-types/culture';

/**
 * Endpoint URLs for the Culture Data Service, used by `fetchCultureSelectorData`.
 *
 * @public
 */
export type CultureSelectorEndpoints = {
  marketsUrl: string;
  localesUrl: string;
  currenciesUrl: string;
};

/**
 * Default relative-path endpoints for the Culture Data Service.
 *
 * @public
 */
export const DEFAULT_CDS_ENDPOINTS: CultureSelectorEndpoints = {
  marketsUrl: '/g/culture-data-service/dataservices/culture/markets/v2.0',
  localesUrl: '/g/culture-data-service/dataservices/culture/locales/v1.0',
  currenciesUrl: '/g/culture-data-service/dataservices/culture/currencies/v1.0',
};

/**
 * @public
 */
export async function fetchJSON<T>(url: string): Promise<T> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      // Thrown for the message to be caught in the parent catch block
      throw new Error(`Status: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    // throw the error to be caught in the custom hook
    throw new Error(`Unable to fetch culture data from URL: ${url}. ${errorMessage}`);
  }
}

function validateCDSResponse(data: any, type: string) {
  if (!data.Items) {
    throw new Error(`Invalid data format after fetching. ${type}`);
  }
}

/**
 * Fetches markets, locales, and currencies from the Culture Data Service.
 * Defaults to relative URLs via `DEFAULT_CDS_ENDPOINTS`. Pass a custom `endpoints` object to override.
 *
 * @public
 * @param localeName - The current locale (e.g. `'en-GB'`).
 * @param showPreRelease - Whether to include pre-release locales.
 * @param endpoints - Optional endpoint URLs. Defaults to `DEFAULT_CDS_ENDPOINTS`.
 *
 * @returns Formatted `currenciesData`, `localesData`, `marketsData`, and `enMarketsData`.
 */
export async function fetchCultureSelectorData(
  localeName: string,
  showPreRelease: boolean,
  endpoints: CultureSelectorEndpoints = DEFAULT_CDS_ENDPOINTS,
): Promise<FetchedCultureData> {
  const results = await Promise.all([
    fetchJSON<CultureCurrencyResponse>(endpoints.currenciesUrl),
    fetchJSON<CultureLocaleResponse>(endpoints.localesUrl),
    fetchJSON<CultureMarketResponse>(`${endpoints.marketsUrl}?localename=${localeName}`),
    fetchJSON<CultureMarketResponse>(`${endpoints.marketsUrl}?localename=en-GB`),
  ]);

  const [
    cultureCurrencyResponse,
    cultureLocaleResponse,
    cultureMarketResponse,
    cultureEnMarketResponse,
  ] = results;

  validateCDSResponse(cultureLocaleResponse, 'locales');
  validateCDSResponse(cultureMarketResponse, 'markets');
  validateCDSResponse(cultureEnMarketResponse, 'markets');
  validateCDSResponse(cultureCurrencyResponse, 'currencies');

  const result = {
    currenciesData: formatCurrencies(cultureCurrencyResponse),
    localesData: formatLocales(cultureLocaleResponse, showPreRelease),
    marketsData: formatMarkets(cultureMarketResponse, { localeName }),
    enMarketsData: formatMarkets(cultureEnMarketResponse),
  };

  return result;
}
